import { contract, core, formatDay, parseDay, schedule, type Wire } from '@map-my-money/engine';
import { changedFrom, itemEnd, largestIncome, valuesOn } from '@map-my-money/views';
import { textMoneyFor, type Region, type TextMoney } from './format.ts';

// Imported by the web app too (its What If replays a run_what_if change set), so nothing here reaches beyond engine and views.
type Plan = Wire.Plan;

/**
 * Plan items at the wire level (the contract's JSON). Everything here is a pure transformation of
 * a Plan document; nothing is validated here, because every write goes through putPlan, which
 * validates the whole document against the contract schema.
 */
export const ITEM_KINDS = ['transaction', 'asset', 'goal', 'property', 'depreciatingAsset'] as const;
export type ItemKind = (typeof ITEM_KINDS)[number];
/** Kinds an assistant may add, change or remove. Legacy property and depreciating-asset records are read-only. */
export const WRITABLE_KINDS = ['transaction', 'asset', 'goal'] as const;
export type WritableKind = (typeof WRITABLE_KINDS)[number];

const COLLECTION = {
  transaction: 'transactions',
  asset: 'assets',
  goal: 'goals',
  property: 'properties',
  depreciatingAsset: 'depreciatingAssets',
} as const satisfies Record<ItemKind, keyof Plan>;

export type WireItem = { readonly id: string; readonly label?: string } & Record<string, unknown>;

export interface Located {
  readonly kind: ItemKind;
  readonly index: number;
  readonly item: WireItem;
}

export const itemsOf = (plan: Plan, kind: ItemKind): readonly WireItem[] => (plan[COLLECTION[kind]] ?? []) as unknown as readonly WireItem[];

export function allItems(plan: Plan): Located[] {
  return ITEM_KINDS.flatMap((kind) => itemsOf(plan, kind).map((item, index) => ({ kind, index, item })));
}

export class ItemError extends Error {
  constructor(
    readonly code: 'not_found' | 'ambiguous' | 'read_only' | 'duplicate_id' | 'linked' | 'invalid_change',
    message: string,
  ) {
    super(message);
  }
}

export function locate(plan: Plan, id: string, kind?: ItemKind): Located {
  const matches = allItems(plan).filter((l) => l.item.id === id && (kind === undefined || l.kind === kind));
  if (matches.length === 0) throw new ItemError('not_found', `No plan item with id "${id}"${kind ? ` of kind ${kind}` : ''}. Use list_plan_items to see the ids.`);
  if (matches.length > 1) throw new ItemError('ambiguous', `Several items have id "${id}" (${matches.map((m) => m.kind).join(', ')}); pass kind.`);
  return matches[0]!;
}

const withCollection = (plan: Plan, kind: ItemKind, items: readonly WireItem[]): Plan => ({ ...plan, [COLLECTION[kind]]: items });

function assertWritable(kind: ItemKind): asserts kind is WritableKind {
  if (!(WRITABLE_KINDS as readonly string[]).includes(kind)) {
    throw new ItemError('read_only', `${kind} items are older-format records; change them in the app.`);
  }
}

const slug = (text: string): string =>
  text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 32) || 'item';

/** A readable id unique across the whole plan: "gym", then "gym-2", … */
export function newId(plan: Plan, label: string | undefined, kind: ItemKind): string {
  const taken = new Set(allItems(plan).map((l) => l.item.id));
  const base = slug(label ?? kind);
  if (!taken.has(base)) return base;
  for (let n = 2; ; n++) if (!taken.has(`${base}-${n}`)) return `${base}-${n}`;
}

export function addItem(plan: Plan, kind: ItemKind, item: Record<string, unknown>): { plan: Plan; item: WireItem } {
  assertWritable(kind);
  const id = typeof item.id === 'string' && item.id.length > 0 ? item.id : newId(plan, typeof item.label === 'string' ? item.label : undefined, kind);
  if (allItems(plan).some((l) => l.item.id === id)) throw new ItemError('duplicate_id', `An item with id "${id}" already exists; omit id to get a fresh one.`);
  const created = { ...item, id } as WireItem;
  return { plan: withCollection(plan, kind, [...itemsOf(plan, kind), created]), item: created };
}

export function updateItem(
  plan: Plan,
  id: string,
  kind: ItemKind | undefined,
  set: Record<string, unknown>,
  unset: readonly string[] = [],
): { plan: Plan; before: WireItem; after: WireItem; kind: ItemKind } {
  const found = locate(plan, id, kind);
  assertWritable(found.kind);
  if ('id' in set && set.id !== id) throw new ItemError('invalid_change', 'An item id cannot be changed; remove it and add a new one.');
  if (unset.includes('id')) throw new ItemError('invalid_change', 'An item id cannot be removed.');
  const after = Object.fromEntries(
    Object.entries({ ...found.item, ...set }).filter(([k, v]) => v !== undefined && !unset.includes(k)),
  ) as WireItem;
  const items = itemsOf(plan, found.kind).map((it, i) => (i === found.index ? after : it));
  return { plan: withCollection(plan, found.kind, items), before: found.item, after, kind: found.kind };
}

/** Items that point at `id` (debts and costs linked to an asset). */
export const linkedTo = (plan: Plan, id: string): WireItem[] =>
  itemsOf(plan, 'transaction').filter((t) => t.linkedAssetId === id);

/** An employer match whose income is gone follows the largest income again, rather than naming nothing. */
function withoutMatchLinkTo(plan: Plan, id: string): Plan {
  const transactions = itemsOf(plan, 'transaction');
  if (!transactions.some((t) => t.employerMatchIncomeId === id)) return plan;
  return withCollection(plan, 'transaction', transactions.map((t) => {
    if (t.employerMatchIncomeId !== id) return t;
    const { employerMatchIncomeId: _, ...rest } = t;
    return rest as WireItem;
  }));
}

export function removeItem(
  plan: Plan,
  id: string,
  kind: ItemKind | undefined,
  { removeLinked }: { removeLinked: boolean },
): { plan: Plan; removed: Located[] } {
  const found = locate(plan, id, kind);
  assertWritable(found.kind);
  const linked = found.kind === 'asset' ? linkedTo(plan, id) : [];
  if (linked.length > 0 && !removeLinked) {
    throw new ItemError(
      'linked',
      `"${label(found.item)}" has linked items (${linked.map((t) => `${t.id}`).join(', ')}). Remove or relink them first, or pass remove_linked: true to remove them too.`,
    );
  }
  let next = withCollection(plan, found.kind, itemsOf(plan, found.kind).filter((_, i) => i !== found.index));
  const linkedIds = new Set(linked.map((t) => t.id));
  if (linkedIds.size > 0) next = withCollection(next, 'transaction', itemsOf(next, 'transaction').filter((t) => !linkedIds.has(t.id)));
  if (found.kind === 'transaction') next = withoutMatchLinkTo(next, id);
  const removed = [found, ...linked.map((item) => ({ kind: 'transaction' as const, index: -1, item }))];
  return { plan: next, removed };
}

// MARK: Descriptions (for summaries, never parsed)

export const label = (item: WireItem): string => (typeof item.label === 'string' && item.label.length > 0 ? item.label : item.id);

const FREQUENCY_WORDS: Readonly<Record<Region, Readonly<Record<string, string>>>> = {
  uk: { oneTime: 'once' },
  us: { oneTime: 'once', fortnightly: 'every two weeks' },
};

function frequencyText(frequency: unknown, region: Region): string {
  if (frequency === undefined) return 'monthly';
  if (typeof frequency === 'string') return FREQUENCY_WORDS[region][frequency] ?? frequency;
  const f = frequency as Wire.Transaction['frequency'] & { interval?: number; unit?: string };
  return `every ${f.interval} ${f.unit}s`;
}

/** "expense “Gym”, £45 monthly from 2026-10-01", in the plan's currency and words. */
export function describe(kind: ItemKind, item: WireItem, { compact, region }: TextMoney): string {
  const name = `“${label(item)}”`;
  switch (kind) {
    case 'transaction': {
      const t = item as unknown as Wire.Transaction;
      const inactive = t.isActive === false ? ', paused' : '';
      return `${t.type} ${name}, ${compact(t.amount)} ${frequencyText(t.frequency, region)} from ${t.startDate}${inactive}`;
    }
    case 'asset': {
      const a = item as unknown as Wire.Asset;
      return `${a.assetClass} ${name}, bought for ${compact(a.purchasePrice)} on ${a.purchaseDate}`;
    }
    case 'goal': {
      const g = item as unknown as Wire.Goal;
      const verb = g.operation === 'crossunder' ? 'at or below' : g.operation === 'equal' ? 'at' : 'reach';
      return `goal ${name}: ${g.series} ${verb} ${compact(g.targetValue)}`;
    }
    case 'property':
      return `property ${name}`;
    case 'depreciatingAsset':
      return `asset ${name}`;
  }
}

/** "change expense “Groceries” to £350 monthly from 2026-10-10" (from its start: "… from the start"). */
function describeChange(item: WireItem, from: string, { compact, region }: TextMoney): string {
  const t = item as unknown as Wire.Transaction;
  const when = from <= t.startDate ? 'from the start' : `from ${from}`;
  return `change ${t.type} “${label(item)}” to ${compact(t.amount)} ${frequencyText(t.frequency, region)} ${when}`;
}

// MARK: Hypothetical changes (run_what_if / apply_what_if)

/**
 * When a step takes effect (TIMELINE R24): a day, a number of months from today, or the day after
 * another item's last payment, which is read from the engine when the step runs, so it follows
 * that item when it changes.
 */
export type When = string | { readonly in_months: number } | { readonly when_ends: string };

export interface ChangeUpdate {
  readonly id: string;
  readonly kind?: ItemKind;
  readonly set?: Record<string, unknown>;
  readonly unset?: readonly string[];
  /** From when the change counts; today when left out. Days before it keep the item as it was. */
  readonly from?: When;
  /** Changes the amount by this percent of the amount in force on `from` ("10" is +10%, "-10" is −10%). */
  readonly by_percent?: string | number;
  /** Sets an investment's contribution to this percent of an income's pay for the same period. */
  readonly percent_of_pay?: string | number;
  /** The income `percent_of_pay` follows; the item's matched income, else the largest. */
  readonly income?: string;
}

export interface ChangeRedirect {
  /** The item whose payment is freed when it ends: a debt, or a transaction with an end. */
  readonly from: string;
  /** The investment (a new payment into it) or debt (a larger payment) the freed payment goes to. */
  readonly to: string;
  readonly when?: 'ends';
}

export interface PlanChangeSet {
  readonly add?: readonly { readonly kind: WritableKind; readonly item: Record<string, unknown>; readonly from?: When }[];
  readonly update?: readonly ChangeUpdate[];
  /** With `from`, a transaction stops then (its last day is the day before); without, it is removed altogether. */
  readonly remove?: readonly { readonly id: string; readonly kind?: ItemKind; readonly from?: When }[];
  readonly redirect?: readonly ChangeRedirect[];
  readonly timeframe_years?: number;
}

export interface AppliedChanges {
  readonly plan: Plan;
  readonly described: string[];
  /** Each changed transaction's effective day, for the write (withDatedEdits keeps them as dated). */
  readonly changesFrom: Readonly<Record<string, string>>;
}

const TRANSACTION_FIELDS = new Set(['type', 'amount', 'frequency', 'startDate', 'endDate', 'endAfterOccurrences', 'annualIncreasePercent', 'employerMatch', 'interestRate', 'debtCurrentBalance', 'history']);
const ASSET_FIELDS = new Set(['purchasePrice', 'purchaseDate', 'assetClass', 'valueTrajectory', 'saleEvent']);
const GOAL_FIELDS = new Set(['series', 'operation', 'targetValue', 'startingValue', 'priority', 'targetDate']);

/**
 * The kind a step's fields can only belong to, so an id shared by an expense and an asset ("car")
 * still names one item: amounts, percents and redirects are transactions, a price an asset.
 */
export function kindFromFields(fields: Readonly<Record<string, unknown>> = {}, transactional = false): ItemKind | undefined {
  if (transactional) return 'transaction';
  const keys = Object.keys(fields);
  if (keys.some((k) => ASSET_FIELDS.has(k))) return 'asset';
  if (keys.some((k) => TRANSACTION_FIELDS.has(k))) return 'transaction';
  if (keys.some((k) => GOAL_FIELDS.has(k))) return 'goal';
  return undefined;
}

/** `kind:id` ("asset:car") names the kind in the id itself. */
function qualified(id: string, kind: ItemKind | undefined): { id: string; kind: ItemKind | undefined } {
  const match = /^([a-zA-Z]+):(.+)$/.exec(id);
  if (match && (ITEM_KINDS as readonly string[]).includes(match[1]!)) return { id: match[2]!, kind: match[1] as ItemKind };
  return { id, kind };
}

/** `locate` that settles a clash between kinds by what the step does, before asking for kind. */
export function locateFor(plan: Plan, id: string, kind: ItemKind | undefined, hint: ItemKind | undefined): Located {
  const q = qualified(id, kind);
  if (q.kind !== undefined || hint === undefined) return locate(plan, q.id, q.kind);
  const matches = allItems(plan).filter((l) => l.item.id === q.id);
  return matches.length > 1 ? locate(plan, q.id, hint) : locate(plan, q.id);
}

const ISO = /^\d{4}-\d{2}-\d{2}$/;

function resolveWhen(plan: Plan, when: When | undefined, today: string | undefined): string | undefined {
  if (when === undefined) return today;
  if (typeof when === 'string') {
    if (!ISO.test(when)) throw new ItemError('invalid_change', `"${when}" is not a day (YYYY-MM-DD).`);
    return when;
  }
  if ('in_months' in when) {
    if (today === undefined) throw new ItemError('invalid_change', 'A change dated in months from now needs today.');
    return formatDay(core.addMonths(parseDay(today), when.in_months));
  }
  const ending = qualified(when.when_ends, undefined).id;
  const end = itemEnd(plan, ending, today ?? plan.configuration.initialBalanceDate ?? '1970-01-01');
  if (end === null) throw new ItemError('invalid_change', `"${ending}" has no last payment to start from: it never ends (give it an end date, or date the change).`);
  return formatDay(core.addDays(parseDay(end.last), 1));
}

const decimalText = (n: number): string => String(Math.round(n * 100) / 100);
const percentOf = (value: string | number): number => {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) throw new ItemError('invalid_change', `"${value}" is not a percent.`);
  return n;
};

/** The fields a percent step sets: the amount in force on `from` scaled, or a share of an income's pay for the same period. */
function percentFields(plan: Plan, item: WireItem, u: ChangeUpdate, from: string): Record<string, unknown> {
  const t = item as unknown as Wire.Transaction;
  if (u.by_percent !== undefined) {
    const base = Number(valuesOn(t, from).amount);
    return { amount: decimalText(base * (1 + percentOf(u.by_percent) / 100)) };
  }
  if (u.percent_of_pay === undefined) return {};
  if (t.type !== 'investment') throw new ItemError('invalid_change', `percent_of_pay sets an investment's contribution; "${t.id}" is ${t.type === 'income' ? 'an' : 'a'} ${t.type}.`);
  const incomeId = u.income ?? t.employerMatchIncomeId ?? largestIncome(plan, parseDay(from))?.id;
  const income = (plan.transactions ?? []).find((x) => x.id === incomeId && x.type === 'income');
  if (!income) throw new ItemError('invalid_change', u.income ? `"${u.income}" is not an income in this plan.` : 'There is no income for percent_of_pay to follow.');
  const pay = Number(valuesOn(income, from).amount) * schedule.annualOccurrences(contract.decodeFrequency(income.frequency));
  const periods = schedule.annualOccurrences(contract.decodeFrequency(t.frequency));
  return {
    amount: decimalText((pay * percentOf(u.percent_of_pay)) / 100 / periods),
    ...(income.annualIncreasePercent === undefined ? {} : { annualIncreasePercent: income.annualIncreasePercent }),
  };
}

function datedUpdate(plan: Plan, u: ChangeUpdate, today: string | undefined): { plan: Plan; line: string; id: string; from: string | null } {
  const percent = u.by_percent !== undefined || u.percent_of_pay !== undefined;
  if (percent && u.set && 'amount' in u.set) throw new ItemError('invalid_change', 'Set an amount or a percent, not both.');
  const found = locateFor(plan, u.id, u.kind, kindFromFields(u.set, percent));
  assertWritable(found.kind);
  const from = found.kind === 'transaction' ? resolveWhen(plan, u.from, today) : undefined;
  if (found.kind === 'transaction' && from === undefined) throw new ItemError('invalid_change', `A change to "${found.item.id}" needs a from day (or today).`);
  const set = { ...(u.set ?? {}), ...(percent ? percentFields(plan, found.item, u, from!) : {}) };
  const updated = updateItem(plan, found.item.id, found.kind, set, u.unset ?? []);
  if (found.kind !== 'transaction') return { plan: updated.plan, line: `change ${describe(updated.kind, updated.after, textMoneyFor(plan))}`, id: found.item.id, from: null };
  const dated = changedFrom(found.item as unknown as Wire.Transaction, updated.after as unknown as Wire.Transaction, from!) as unknown as WireItem;
  const items = itemsOf(updated.plan, 'transaction').map((it) => (it.id === found.item.id ? dated : it));
  return { plan: withCollection(updated.plan, 'transaction', items), line: describeChange(dated, from!, textMoneyFor(plan)), id: found.item.id, from: from! };
}

function datedRemoval(plan: Plan, r: NonNullable<PlanChangeSet['remove']>[number], today: string | undefined): { plan: Plan; line: string } {
  if (r.from === undefined) {
    const q = qualified(r.id, r.kind);
    const result = removeItem(plan, q.id, q.kind, { removeLinked: false });
    return { plan: result.plan, line: `remove ${describe(result.removed[0]!.kind, result.removed[0]!.item, textMoneyFor(plan))}` };
  }
  const found = locateFor(plan, r.id, r.kind, 'transaction');
  if (found.kind !== 'transaction') throw new ItemError('invalid_change', 'Only a transaction can stop from a day; leave out from to remove it.');
  const from = resolveWhen(plan, r.from, today)!;
  const t = found.item as unknown as Wire.Transaction;
  if (from <= t.startDate) return datedRemoval(plan, { id: t.id, kind: 'transaction' }, today);
  const last = formatDay(core.addDays(parseDay(from), -1));
  const result = updateItem(plan, t.id, 'transaction', { endDate: last }, ['endAfterOccurrences']);
  const { compact, region } = textMoneyFor(plan);
  return { plan: result.plan, line: `stop ${t.type} “${label(found.item)}” (${compact(t.amount)} ${frequencyText(t.frequency, region)}) after ${last}` };
}

function redirect(plan: Plan, step: ChangeRedirect, today: string | undefined): { plan: Plan; line: string; id: string; from: string | null } {
  const source = locateFor(plan, step.from, undefined, 'transaction');
  const target = locateFor(plan, step.to, undefined, 'transaction');
  if (source.kind !== 'transaction' || target.kind !== 'transaction') throw new ItemError('invalid_change', 'A redirect goes from one transaction to another.');
  const x = source.item as unknown as Wire.Transaction;
  const y = target.item as unknown as Wire.Transaction;
  if (y.type !== 'investment' && y.type !== 'debt') throw new ItemError('invalid_change', `A redirect sends a payment into an investment or a debt; "${y.id}" is ${y.type === 'income' ? 'an' : 'a'} ${y.type}.`);
  const end = itemEnd(plan, x.id, today ?? plan.configuration.initialBalanceDate ?? '1970-01-01');
  if (end === null) throw new ItemError('invalid_change', `"${x.id}" never ends, so there is no payment to redirect (give it an end date).`);
  const freed = valuesOn(x, end.last).amount ?? x.amount;
  const { compact } = textMoneyFor(plan);
  if (y.type === 'debt') {
    const larger = { ...y, amount: decimalText(Number(valuesOn(y, end.next).amount) + Number(freed)) };
    const dated = changedFrom(y, larger, end.next) as unknown as WireItem;
    const items = itemsOf(plan, 'transaction').map((it) => (it.id === y.id ? dated : it));
    return { plan: withCollection(plan, 'transaction', items), line: `when “${label(source.item)}” ends, add its ${compact(freed)} to “${label(target.item)}” from ${end.next}`, id: y.id, from: end.next };
  }
  const added: Record<string, unknown> = {
    id: newId(plan, `${y.id}~${x.id}`, 'transaction'),
    label: `${label(source.item)} → ${label(target.item)}`,
    type: 'investment',
    amount: freed,
    frequency: x.frequency ?? 'monthly',
    startDate: end.next,
    ...(y.interestRate === undefined ? {} : { interestRate: y.interestRate }),
    ...(y.taxRelief === undefined ? {} : { taxRelief: y.taxRelief }),
  };
  const result = addItem(plan, 'transaction', added);
  return { plan: result.plan, line: `when “${label(source.item)}” ends, pay its ${compact(freed)} into “${label(target.item)}” from ${end.next}`, id: result.item.id, from: null };
}

const endsDated = (when: When | undefined): boolean => typeof when === 'object' && when !== null && 'when_ends' in when;

/**
 * Applies a change set and describes each step. Order: removals, updates and additions dated by a
 * day, then the steps dated by another item's end and the redirects (so they read that item as
 * changed), then the timeframe. A transaction's change counts from its `from` (today when left
 * out): the days before keep what the item was (R24).
 */
export function applyChangeSet(plan: Plan, changes: PlanChangeSet, options: { readonly today?: string } = {}): AppliedChanges {
  const { today } = options;
  let next = plan;
  const described: string[] = [];
  const changesFrom: Record<string, string> = {};
  const late: (() => void)[] = [];
  const update = (u: ChangeUpdate) => {
    const result = datedUpdate(next, u, today);
    next = result.plan;
    described.push(result.line);
    if (result.from !== null) changesFrom[result.id] = result.from;
  };
  for (const r of changes.remove ?? []) {
    const step = () => {
      const result = datedRemoval(next, r, today);
      next = result.plan;
      described.push(result.line);
    };
    if (endsDated(r.from)) late.push(step);
    else step();
  }
  for (const u of changes.update ?? []) {
    if (endsDated(u.from)) late.push(() => update(u));
    else update(u);
  }
  for (const a of changes.add ?? []) {
    const step = () => {
      const day = a.from === undefined ? undefined : resolveWhen(next, a.from, today);
      const dateKey = a.kind === 'asset' ? 'purchaseDate' : a.kind === 'transaction' ? 'startDate' : null;
      const item = day !== undefined && dateKey !== null ? { ...a.item, [dateKey]: day } : a.item;
      const result = addItem(next, a.kind, item);
      next = result.plan;
      described.push(`add ${describe(a.kind, result.item, textMoneyFor(next))}`);
    };
    if (endsDated(a.from)) late.push(step);
    else step();
  }
  for (const step of late) step();
  for (const r of changes.redirect ?? []) {
    const result = redirect(next, r, today);
    next = result.plan;
    described.push(result.line);
    if (result.from !== null) changesFrom[result.id] = result.from;
  }
  if (changes.timeframe_years !== undefined && changes.timeframe_years !== next.configuration.timeframeYears) {
    next = { ...next, configuration: { ...next.configuration, timeframeYears: changes.timeframe_years } };
    described.push(`set the forecast horizon to ${changes.timeframe_years} years`);
  }
  return { plan: next, described, changesFrom };
}
